/**
 * Procedural one-shot cue routing.
 *
 * These functions only operate on an AudioContext-like object supplied through ShotBuilder.
 * No host globals or file assets are touched, so the routing remains unit-testable in Node.
 */

import { clamp } from '../core/index.js';
import { envAD, envAHR, envSwell, glide } from './dsp.js';

export const CUE_NAMES = Object.freeze([
  'swing-light', 'swing-heavy', 'swing-whoosh',
  'impact-light', 'impact-medium', 'impact-heavy', 'impact-counter', 'impact-body', 'impact-head',
  'guard-light', 'guard-heavy', 'guard-break', 'parry',
  'throw-grab', 'throw-slam', 'throw-break',
  'wall-impact', 'prop-break', 'footstep', 'cloth', 'dash', 'sidestep', 'land-soft', 'land-hard',
  'exert-light', 'exert-heavy', 'exert-pain', 'exert-ko',
  'round-announce', 'ko-announce',
  'ui-move', 'ui-confirm', 'ui-cancel', 'ui-error',
  'crowd-swell', 'crowd-gasp', 'music-intensity',
]);

export function cueFamily(cue) {
  if (cue.startsWith('impact-')) return 'impact';
  if (cue.startsWith('swing-') || cue === 'cloth' || cue === 'dash' || cue === 'sidestep') {
    return 'movement';
  }
  if (cue.startsWith('guard-') || cue === 'parry') return 'guard';
  if (cue.startsWith('throw-')) return 'throw';
  if (cue === 'wall-impact' || cue === 'prop-break' || cue === 'footstep'
      || cue === 'land-soft' || cue === 'land-hard') return 'surface';
  if (cue.startsWith('exert-')) return 'voice';
  if (cue === 'round-announce' || cue === 'ko-announce') return 'announce';
  if (cue.startsWith('ui-')) return 'ui';
  if (cue.startsWith('crowd-')) return 'crowd';
  if (cue === 'music-intensity') return 'music';
  return 'impact';
}

function connectOutput(node, destination, send) {
  node.connect(destination);
  if (send) node.connect(send);
}

function impactWeight(cue) {
  if (cue === 'impact-counter') return 1.35;
  if (cue === 'impact-heavy' || cue === 'impact-head') return 1.12;
  if (cue === 'impact-medium' || cue === 'impact-body') return 0.82;
  return 0.55;
}

function movementWeight(cue) {
  if (cue === 'swing-heavy') return 1.05;
  if (cue === 'swing-whoosh' || cue === 'dash') return 0.82;
  if (cue === 'sidestep') return 0.58;
  if (cue === 'cloth') return 0.36;
  return 0.5;
}

function renderImpact(builder, cue, o) {
  const w = impactWeight(cue);
  const dur = 0.075 + w * 0.115;
  const out = builder.gain();
  const body = builder.filter('bandpass', 520 - w * 190, 0.58 + w * 0.28);
  const crack = builder.filter('highpass', 1150 + w * 620, 0.55);
  const pan = builder.pan(o.pan);
  const noiseBody = builder.noise('brown', 0.9 + o.pitch * 0.12, o.when, dur);
  const noiseCrack = builder.noise('velvet', 0.8 + o.pitch * 0.24, o.when, dur * 0.72);
  const thump = builder.osc('sine', (108 - w * 34) * o.pitch, o.when, dur);
  const thumpGain = builder.gain();
  noiseBody.connect(body).connect(out);
  noiseCrack.connect(crack).connect(out);
  thump.connect(thumpGain).connect(out);
  out.connect(pan);
  connectOutput(pan, o.destination, o.send);
  envAD(out.gain, o.when, o.gain * (0.22 + w * 0.22), 0.0007, dur);
  envAD(thumpGain.gain, o.when, o.gain * (0.18 + w * 0.2), 0.0006, dur * 0.8);
  glide(thump.frequency, o.when, (128 - w * 28) * o.pitch, (55 - w * 9) * o.pitch, dur);
  return dur;
}

function renderMovement(builder, cue, o) {
  const w = movementWeight(cue);
  const dur = 0.07 + w * 0.19;
  const out = builder.gain();
  const filter = builder.filter('bandpass', 920 + w * 1250, 0.45 + w * 0.65);
  const pan = builder.pan(o.pan);
  const source = builder.noise(cue === 'cloth' ? 'pink' : 'white', 0.72 + o.pitch * 0.36, o.when, dur);
  source.connect(filter).connect(out).connect(pan);
  connectOutput(pan, o.destination, o.send);
  envSwell(out.gain, o.when, o.gain * (0.08 + w * 0.18), dur, 0.38);
  glide(filter.frequency, o.when, 480 + w * 900, 1800 + w * 2600, dur * 0.64);
  return dur;
}

function renderGuard(builder, cue, o) {
  const heavy = cue === 'guard-heavy' || cue === 'guard-break';
  const parry = cue === 'parry';
  const dur = parry ? 0.22 : heavy ? 0.17 : 0.11;
  const out = builder.gain();
  const metal = builder.filter('bandpass', parry ? 3100 : heavy ? 1900 : 2450, parry ? 8 : 4.5);
  const pan = builder.pan(o.pan);
  const noise = builder.noise('velvet', 0.9 + o.pitch * 0.2, o.when, dur);
  const tone = builder.osc(parry ? 'sine' : 'triangle', (parry ? 1120 : heavy ? 430 : 690) * o.pitch, o.when, dur);
  const toneGain = builder.gain();
  noise.connect(metal).connect(out);
  tone.connect(toneGain).connect(out);
  out.connect(pan);
  connectOutput(pan, o.destination, o.send);
  envAD(out.gain, o.when, o.gain * (parry ? 0.3 : heavy ? 0.28 : 0.2), 0.0005, dur);
  envAD(toneGain.gain, o.when, o.gain * (parry ? 0.2 : 0.12), 0.0005, dur * 0.82);
  glide(tone.frequency, o.when, tone.frequency.value * 1.08, tone.frequency.value * 0.72, dur);
  return dur;
}

function renderThrow(builder, cue, o) {
  const slam = cue === 'throw-slam';
  const broken = cue === 'throw-break';
  const dur = slam ? 0.34 : broken ? 0.18 : 0.13;
  const out = builder.gain();
  const filter = builder.filter('lowpass', slam ? 720 : 1350, 0.72);
  const pan = builder.pan(o.pan);
  const noise = builder.noise(slam ? 'brown' : 'pink', 0.86 + o.pitch * 0.1, o.when, dur);
  const tone = builder.osc('sine', (slam ? 72 : broken ? 155 : 118) * o.pitch, o.when, dur);
  const toneGain = builder.gain();
  noise.connect(filter).connect(out);
  tone.connect(toneGain).connect(out);
  out.connect(pan);
  connectOutput(pan, o.destination, o.send);
  envAD(out.gain, o.when, o.gain * (slam ? 0.42 : 0.2), 0.0008, dur);
  envAD(toneGain.gain, o.when, o.gain * (slam ? 0.34 : 0.16), 0.0007, dur * 0.86);
  glide(tone.frequency, o.when, tone.frequency.value * 1.15, tone.frequency.value * 0.56, dur);
  return dur;
}

function renderSurface(builder, cue, o) {
  const hard = cue === 'wall-impact' || cue === 'prop-break' || cue === 'land-hard';
  const foot = cue === 'footstep' || cue === 'land-soft';
  const dur = hard ? 0.24 : foot ? 0.105 : 0.14;
  const out = builder.gain();
  const filter = builder.filter(hard ? 'bandpass' : 'lowpass', hard ? 640 : 390, hard ? 1.3 : 0.65);
  const pan = builder.pan(o.pan);
  const noise = builder.noise(hard ? 'velvet' : 'brown', 0.82 + o.pitch * 0.14, o.when, dur);
  noise.connect(filter).connect(out).connect(pan);
  connectOutput(pan, o.destination, o.send);
  envAD(out.gain, o.when, o.gain * (hard ? 0.38 : 0.17), 0.0008, dur);
  return dur;
}

function renderVoice(builder, cue, o) {
  const heavy = cue === 'exert-heavy' || cue === 'exert-ko';
  const pain = cue === 'exert-pain' || cue === 'exert-ko';
  const dur = cue === 'exert-ko' ? 0.58 : heavy ? 0.32 : 0.2;
  const out = builder.gain();
  const throat = builder.filter('bandpass', pain ? 760 : 1040, 1.25);
  const chest = builder.filter('peaking', heavy ? 220 : 310, 0.8, heavy ? 5 : 2);
  const pan = builder.pan(o.pan * 0.78);
  const source = builder.noise('voice', (heavy ? 0.68 : 0.92) * o.pitch, o.when, dur);
  source.connect(throat).connect(chest).connect(out).connect(pan);
  connectOutput(pan, o.destination, o.send);
  envAHR(out.gain, o.when, o.gain * (heavy ? 0.23 : 0.15), 0.012, dur * 0.34, dur * 0.54);
  glide(throat.frequency, o.when, pain ? 1280 : 980, pain ? 520 : 720, dur);
  return dur;
}

function renderAnnounce(builder, cue, o) {
  const ko = cue === 'ko-announce';
  const dur = ko ? 0.72 : 0.5;
  const out = builder.gain();
  const pan = builder.pan(0);
  const low = builder.osc('sawtooth', (ko ? 72 : 96) * o.pitch, o.when, dur);
  const high = builder.osc('triangle', (ko ? 144 : 192) * o.pitch, o.when + 0.025, dur * 0.9);
  const lowFilter = builder.filter('lowpass', ko ? 620 : 880, 0.8);
  low.connect(lowFilter).connect(out);
  high.connect(out);
  out.connect(pan);
  connectOutput(pan, o.destination, o.send);
  envAHR(out.gain, o.when, o.gain * (ko ? 0.24 : 0.16), 0.015, dur * 0.35, dur * 0.52);
  glide(low.frequency, o.when, low.frequency.value * 1.12, low.frequency.value * 0.72, dur);
  return dur;
}

function renderUi(builder, cue, o) {
  const error = cue === 'ui-error';
  const confirm = cue === 'ui-confirm';
  const dur = error ? 0.16 : 0.09;
  const out = builder.gain();
  const pan = builder.pan(0);
  const tone = builder.osc(error ? 'square' : 'sine', (error ? 128 : confirm ? 620 : 430) * o.pitch, o.when, dur);
  tone.connect(out).connect(pan);
  connectOutput(pan, o.destination, null);
  envAD(out.gain, o.when, o.gain * (error ? 0.11 : 0.075), 0.001, dur);
  if (!error) glide(tone.frequency, o.when, tone.frequency.value, tone.frequency.value * (confirm ? 1.42 : 0.86), dur);
  return dur;
}

function renderCrowd(builder, cue, o) {
  const gasp = cue === 'crowd-gasp';
  const dur = gasp ? 0.42 : 0.75;
  const out = builder.gain();
  const filter = builder.filter('bandpass', gasp ? 980 : 560, gasp ? 0.75 : 0.42);
  const source = builder.noise('crowd', gasp ? 1.18 : 0.88, o.when, dur);
  source.connect(filter).connect(out);
  connectOutput(out, o.destination, o.send);
  envSwell(out.gain, o.when, o.gain * (gasp ? 0.14 : 0.2), dur, gasp ? 0.18 : 0.56);
  return dur;
}

/**
 * Builds one cue and returns its exact scheduled duration.
 */
export function renderCue(builder, cue, options) {
  const family = cueFamily(cue);
  const o = {
    when: options.when,
    gain: clamp(options.gain, 0, 1.5),
    pitch: clamp(options.pitch, 0.5, 1.8),
    pan: clamp(options.pan, -0.9, 0.9),
    destination: options.destination,
    send: options.send || null,
  };
  if (family === 'movement') return renderMovement(builder, cue, o);
  if (family === 'guard') return renderGuard(builder, cue, o);
  if (family === 'throw') return renderThrow(builder, cue, o);
  if (family === 'surface') return renderSurface(builder, cue, o);
  if (family === 'voice') return renderVoice(builder, cue, o);
  if (family === 'announce') return renderAnnounce(builder, cue, o);
  if (family === 'ui') return renderUi(builder, cue, o);
  if (family === 'crowd') return renderCrowd(builder, cue, o);
  if (family === 'music') return 0;
  return renderImpact(builder, cue, o);
}
